import React, { useState, useCallback, useRef, useMemo } from "react";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  getBezierPath,
} from "reactflow";
import "reactflow/dist/style.css";
import { Button } from "../ui/button";
import { Plus, Trash, Code, DownloadSimple, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Node type definitions — each has a category, color, input/output ports
// ---------------------------------------------------------------------------
const NODE_DEFS = {
  // ── Events ───────────────────────────────────────────────
  on_start: {
    category: "event", label: "On Start",
    color: "#22d3ee", outputs: [{ id: "exec", label: "▶" }], inputs: [],
    desc: "Runs once at game start",
  },
  on_update: {
    category: "event", label: "On Update",
    color: "#22d3ee", outputs: [{ id: "exec", label: "▶" }], inputs: [],
    desc: "Runs every frame",
  },
  on_key_press: {
    category: "event", label: "On Key Press",
    color: "#22d3ee",
    outputs: [{ id: "exec", label: "▶" }, { id: "key", label: "key" }],
    inputs: [{ id: "key", label: "Key", type: "select", options: ["Space","W","A","S","D","ArrowLeft","ArrowRight","ArrowUp","E","F"] }],
    desc: "Fires when key is pressed",
  },
  on_collision: {
    category: "event", label: "On Collision",
    color: "#22d3ee",
    outputs: [{ id: "exec", label: "▶" }, { id: "other", label: "other" }],
    inputs: [{ id: "tag", label: "Tag", type: "text", placeholder: "Enemy" }],
    desc: "Fires on collision with tagged object",
  },
  // ── Actions ──────────────────────────────────────────────
  move: {
    category: "action", label: "Move",
    color: "#84cc16",
    inputs: [{ id: "exec", label: "▶" }, { id: "speed", label: "Speed", type: "number", placeholder: "200" }, { id: "dir", label: "Dir", type: "select", options: ["left","right","up","down","auto_horizontal","auto_all"] }],
    outputs: [{ id: "exec", label: "▶" }],
    desc: "Move the entity",
  },
  jump: {
    category: "action", label: "Jump",
    color: "#84cc16",
    inputs: [{ id: "exec", label: "▶" }, { id: "force", label: "Force", type: "number", placeholder: "450" }],
    outputs: [{ id: "exec", label: "▶" }],
    desc: "Apply upward jump velocity",
  },
  take_damage: {
    category: "action", label: "Take Damage",
    color: "#f97316",
    inputs: [{ id: "exec", label: "▶" }, { id: "amount", label: "Amount", type: "number", placeholder: "10" }],
    outputs: [{ id: "exec", label: "▶" }],
    desc: "Reduce HP",
  },
  play_sound: {
    category: "action", label: "Play Sound",
    color: "#a78bfa",
    inputs: [{ id: "exec", label: "▶" }, { id: "key", label: "Key", type: "text", placeholder: "jump_sfx" }],
    outputs: [{ id: "exec", label: "▶" }],
    desc: "Play an audio clip",
  },
  spawn: {
    category: "action", label: "Spawn Object",
    color: "#84cc16",
    inputs: [{ id: "exec", label: "▶" }, { id: "key", label: "Prefab key", type: "text", placeholder: "bullet" }, { id: "x", label: "X", type: "number", placeholder: "0" }, { id: "y", label: "Y", type: "number", placeholder: "0" }],
    outputs: [{ id: "exec", label: "▶" }, { id: "obj", label: "spawned" }],
    desc: "Instantiate an object",
  },
  destroy: {
    category: "action", label: "Destroy",
    color: "#f97316",
    inputs: [{ id: "exec", label: "▶" }],
    outputs: [{ id: "exec", label: "▶" }],
    desc: "Destroy this object",
  },
  add_score: {
    category: "action", label: "Add Score",
    color: "#facc15",
    inputs: [{ id: "exec", label: "▶" }, { id: "amount", label: "Amount", type: "number", placeholder: "10" }],
    outputs: [{ id: "exec", label: "▶" }],
    desc: "Increment player score",
  },
  // ── Conditions ───────────────────────────────────────────
  if_hp: {
    category: "condition", label: "If HP ≤",
    color: "#f59e0b",
    inputs: [{ id: "exec", label: "▶" }, { id: "threshold", label: "Threshold", type: "number", placeholder: "0" }],
    outputs: [{ id: "true", label: "TRUE ▶" }, { id: "false", label: "FALSE ▶" }],
    desc: "Branch on HP value",
  },
  if_grounded: {
    category: "condition", label: "If On Ground",
    color: "#f59e0b",
    inputs: [{ id: "exec", label: "▶" }],
    outputs: [{ id: "true", label: "TRUE ▶" }, { id: "false", label: "FALSE ▶" }],
    desc: "Branch if entity is on ground",
  },
  // ── Variables ────────────────────────────────────────────
  set_var: {
    category: "variable", label: "Set Variable",
    color: "#ec4899",
    inputs: [{ id: "exec", label: "▶" }, { id: "name", label: "Name", type: "text", placeholder: "myVar" }, { id: "value", label: "Value", type: "text", placeholder: "0" }],
    outputs: [{ id: "exec", label: "▶" }],
    desc: "Set a variable value",
  },
  get_var: {
    category: "variable", label: "Get Variable",
    color: "#ec4899",
    inputs: [{ id: "name", label: "Name", type: "text", placeholder: "myVar" }],
    outputs: [{ id: "value", label: "value" }],
    desc: "Read a variable",
  },
};

const CATEGORY_ORDER = ["event","action","condition","variable"];
const CATEGORY_LABELS = { event:"Eventos", action:"Acciones", condition:"Condiciones", variable:"Variables" };

// ---------------------------------------------------------------------------
// Custom node renderer
// ---------------------------------------------------------------------------
function GFNode({ id, data, selected }) {
  const def = NODE_DEFS[data.type] || {};
  const inputs  = def.inputs  || [];
  const outputs = def.outputs || [];

  return (
    <div
      style={{
        background: "#18181b",
        border: `1.5px solid ${selected ? def.color : "#3f3f46"}`,
        borderRadius: 4,
        minWidth: 180,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        boxShadow: selected ? `0 0 0 1px ${def.color}44` : "none",
      }}
    >
      {/* Header */}
      <div style={{
        background: def.color + "22",
        borderBottom: `1px solid ${def.color}55`,
        padding: "5px 10px",
        display: "flex",
        alignItems: "center",
        gap: 6,
        borderRadius: "2px 2px 0 0",
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: "50%",
          background: def.color, flexShrink: 0,
        }} />
        <span style={{ color: def.color, fontWeight: 600, letterSpacing: "0.05em", fontSize: 11 }}>
          {def.label}
        </span>
      </div>

      {/* Port rows */}
      <div style={{ padding: "6px 0", position: "relative" }}>
        {/* Inputs */}
        {inputs.map((inp, i) => (
          <div key={inp.id} style={{ display: "flex", alignItems: "center", padding: "2px 10px", gap: 6, minHeight: 22, position: "relative" }}>
            <Handle
              type="target"
              position={Position.Left}
              id={inp.id}
              style={{ left: -6, top: "50%", transform: "translateY(-50%)", width: 10, height: 10, background: inp.id === "exec" ? def.color : "#71717a", border: "none", borderRadius: 2 }}
            />
            {inp.id !== "exec" && (
              <>
                <span style={{ color: "#71717a", fontSize: 10, whiteSpace: "nowrap" }}>{inp.label}</span>
                {inp.type === "select" ? (
                  <select
                    value={data.props?.[inp.id] || inp.options[0]}
                    onChange={e => data.onPropChange(inp.id, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={{ background: "#09090b", border: "1px solid #3f3f46", color: "#d4d4d8", fontSize: 10, padding: "1px 4px", borderRadius: 2, marginLeft: "auto" }}
                  >
                    {inp.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type={inp.type || "text"}
                    placeholder={inp.placeholder}
                    value={data.props?.[inp.id] || ""}
                    onChange={e => data.onPropChange(inp.id, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={{ background: "#09090b", border: "1px solid #3f3f46", color: "#d4d4d8", fontSize: 10, padding: "1px 6px", borderRadius: 2, width: 70, marginLeft: "auto" }}
                  />
                )}
              </>
            )}
          </div>
        ))}

        {/* Outputs */}
        {outputs.map((out, i) => (
          <div key={out.id} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "2px 10px", gap: 6, minHeight: 22, position: "relative" }}>
            <span style={{ color: out.id === "exec" || out.id === "true" || out.id === "false" ? def.color : "#71717a", fontSize: 10 }}>
              {out.label}
            </span>
            <Handle
              type="source"
              position={Position.Right}
              id={out.id}
              style={{ right: -6, top: "50%", transform: "translateY(-50%)", width: 10, height: 10, background: out.id.includes("exec") || out.id === "true" || out.id === "false" ? def.color : "#71717a", border: "none", borderRadius: 2 }}
            />
          </div>
        ))}
      </div>

      {/* Description */}
      {def.desc && (
        <div style={{ padding: "2px 10px 6px", color: "#52525b", fontSize: 9, letterSpacing: "0.05em" }}>
          {def.desc}
        </div>
      )}
    </div>
  );
}

const NODE_TYPES = { gfNode: GFNode };

// ---------------------------------------------------------------------------
// Code generator — walks the node graph and emits JavaScript
// ---------------------------------------------------------------------------
function generateCodeFromGraph(nodes, edges) {
  const lines = [
    "// ── Generated by GameForge Visual Scripting ──",
    "// Paste inside your Phaser scene update() / create() methods",
    "",
  ];

  // Find all event nodes (entry points)
  const eventNodes = nodes.filter(n => NODE_DEFS[n.data.type]?.category === "event");

  function walkChain(nodeId, depth = 0) {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return [];
    const t = node.data.type;
    const props = node.data.props || {};
    const indent = "  ".repeat(depth);
    const result = [];

    switch (t) {
      case "move":
        const dir = props.dir || "auto_horizontal";
        const spd = props.speed || 200;
        if (dir === "auto_horizontal") {
          result.push(`${indent}if (this.keys.left.isDown) this.player.setVelocityX(-${spd});`);
          result.push(`${indent}else if (this.keys.right.isDown) this.player.setVelocityX(${spd});`);
          result.push(`${indent}else this.player.setVelocityX(0);`);
        } else if (dir === "left")  result.push(`${indent}this.player.setVelocityX(-${spd});`);
        else if (dir === "right") result.push(`${indent}this.player.setVelocityX(${spd});`);
        else if (dir === "up")    result.push(`${indent}this.player.setVelocityY(-${spd});`);
        break;
      case "jump":
        result.push(`${indent}if (this.player.body.blocked.down) this.player.setVelocityY(-${props.force || 450});`);
        break;
      case "take_damage":
        result.push(`${indent}this.playerHp -= ${props.amount || 10};`);
        result.push(`${indent}if (this.playerHp <= 0) this.scene.restart();`);
        break;
      case "play_sound":
        result.push(`${indent}this.sound.play('${props.key || "sfx"}');`);
        break;
      case "spawn":
        result.push(`${indent}this.physics.add.sprite(${props.x||0}, ${props.y||0}, '${props.key||"obj"}');`);
        break;
      case "destroy":
        result.push(`${indent}this.destroy();`);
        break;
      case "add_score":
        result.push(`${indent}this.score = (this.score || 0) + ${props.amount || 10};`);
        result.push(`${indent}this.scoreText?.setText('Score: ' + this.score);`);
        break;
      case "set_var":
        result.push(`${indent}this.vars = this.vars || {};`);
        result.push(`${indent}this.vars['${props.name || "x"}'] = ${isNaN(props.value) ? `'${props.value}'` : props.value || 0};`);
        break;
      case "if_hp":
        result.push(`${indent}if (this.playerHp <= ${props.threshold || 0}) {`);
        const trueEdge = edges.find(e => e.source === nodeId && e.sourceHandle === "true");
        if (trueEdge) walkChain(trueEdge.target, depth + 1).forEach(l => result.push(l));
        result.push(`${indent}} else {`);
        const falseEdge = edges.find(e => e.source === nodeId && e.sourceHandle === "false");
        if (falseEdge) walkChain(falseEdge.target, depth + 1).forEach(l => result.push(l));
        result.push(`${indent}}`);
        return result;
      case "if_grounded":
        result.push(`${indent}if (this.player.body.blocked.down) {`);
        const tEdge = edges.find(e => e.source === nodeId && e.sourceHandle === "true");
        if (tEdge) walkChain(tEdge.target, depth + 1).forEach(l => result.push(l));
        result.push(`${indent}}`);
        return result;
      default:
        break;
    }

    // Follow exec chain
    const nextEdge = edges.find(e => e.source === nodeId && e.sourceHandle === "exec");
    if (nextEdge) walkChain(nextEdge.target, depth).forEach(l => result.push(l));

    return result;
  }

  eventNodes.forEach(eNode => {
    const t = eNode.data.type;
    const props = eNode.data.props || {};
    const nextEdge = edges.find(e => e.source === eNode.id && e.sourceHandle === "exec");

    if (t === "on_start") {
      lines.push("// ── create() ──");
      if (nextEdge) walkChain(nextEdge.target, 1).forEach(l => lines.push(l));
    } else if (t === "on_update") {
      lines.push("// ── update() ──");
      if (nextEdge) walkChain(nextEdge.target, 1).forEach(l => lines.push(l));
    } else if (t === "on_key_press") {
      lines.push(`// ── key: ${props.key || "Space"} ──`);
      lines.push(`if (this.input.keyboard.checkDown(this.keys['${props.key || "Space"}'])) {`);
      if (nextEdge) walkChain(nextEdge.target, 1).forEach(l => lines.push(l));
      lines.push("}");
    } else if (t === "on_collision") {
      lines.push(`// ── collision with: ${props.tag || "Enemy"} ──`);
      lines.push(`this.physics.add.overlap(this.player, this.${(props.tag||"enemies").toLowerCase()}, () => {`);
      if (nextEdge) walkChain(nextEdge.target, 1).forEach(l => lines.push(l));
      lines.push("});");
    }
    lines.push("");
  });

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const DEFAULT_NODES = [
  { id: "1", type: "gfNode", position: { x: 40, y: 80 },  data: { type: "on_update",   props: {}, onPropChange: null } },
  { id: "2", type: "gfNode", position: { x: 280, y: 80 }, data: { type: "move",         props: { speed: "200", dir: "auto_horizontal" }, onPropChange: null } },
  { id: "3", type: "gfNode", position: { x: 40, y: 240 }, data: { type: "on_key_press", props: { key: "Space" }, onPropChange: null } },
  { id: "4", type: "gfNode", position: { x: 280, y: 240 }, data: { type: "if_grounded", props: {}, onPropChange: null } },
  { id: "5", type: "gfNode", position: { x: 500, y: 220 }, data: { type: "jump",        props: { force: "450" }, onPropChange: null } },
];
const DEFAULT_EDGES = [
  { id: "e1-2", source: "1", target: "2", sourceHandle: "exec", targetHandle: "exec", animated: true, style: { stroke: "#22d3ee", strokeWidth: 1.5 } },
  { id: "e3-4", source: "3", target: "4", sourceHandle: "exec", targetHandle: "exec", animated: true, style: { stroke: "#22d3ee", strokeWidth: 1.5 } },
  { id: "e4-5", source: "4", target: "5", sourceHandle: "true", targetHandle: "exec", animated: true, style: { stroke: "#84cc16", strokeWidth: 1.5 } },
];

export default function VisualScriptingPanel({ project, onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(
    (project.visualScript?.nodes || DEFAULT_NODES).map(n => ({
      ...n,
      data: { ...n.data, onPropChange: null },
    }))
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    project.visualScript?.edges || DEFAULT_EDGES
  );
  const [showPalette, setShowPalette] = useState(true);
  const [showCode, setShowCode] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const idRef = useRef(100);

  // Inject prop-change callbacks into node data
  const nodesWithCallbacks = useMemo(() => nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      onPropChange: (propId, val) => {
        setNodes(ns => ns.map(x => x.id === n.id
          ? { ...x, data: { ...x.data, props: { ...x.data.props, [propId]: val } } }
          : x
        ));
      },
    },
  })), [nodes, setNodes]);

  const onConnect = useCallback((params) => {
    const color = params.sourceHandle?.includes("true") ? "#84cc16"
      : params.sourceHandle?.includes("false") ? "#f97316"
      : "#22d3ee";
    setEdges(eds => addEdge({ ...params, animated: true, style: { stroke: color, strokeWidth: 1.5 } }, eds));
  }, [setEdges]);

  const addNode = (type) => {
    const id = String(++idRef.current);
    const def = NODE_DEFS[type];
    setNodes(ns => [...ns, {
      id, type: "gfNode",
      position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
      data: {
        type,
        props: Object.fromEntries((def.inputs || []).filter(i => i.placeholder).map(i => [i.id, i.placeholder])),
        onPropChange: null,
      },
    }]);
  };

  const deleteSelected = () => {
    setNodes(ns => ns.filter(n => !n.selected));
    setEdges(es => es.filter(e => !e.selected));
  };

  const handleSave = () => {
    onChange({ visualScript: { nodes, edges } });
    toast.success("Script visual guardado");
  };

  const handleGenerateCode = () => {
    const code = generateCodeFromGraph(nodes, edges);
    setGeneratedCode(code);
    setShowCode(true);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    toast.success("Código copiado");
  };

  const handleDownloadCode = () => {
    const blob = new Blob([generatedCode], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${project.slug || "game"}-visual-script.js`;
    a.click();
    toast.success("Descargado");
  };

  return (
    <div className="h-full flex">
      {/* Palette sidebar */}
      {showPalette && (
        <aside className="w-52 border-r border-zinc-800 bg-zinc-950 overflow-auto gf-scroll flex-shrink-0">
          <div className="px-3 py-2 border-b border-zinc-800 text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Nodos
          </div>
          {CATEGORY_ORDER.map(cat => (
            <div key={cat}>
              <div className="px-3 pt-3 pb-1 text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                {CATEGORY_LABELS[cat]}
              </div>
              {Object.entries(NODE_DEFS)
                .filter(([,d]) => d.category === cat)
                .map(([key, def]) => (
                  <button
                    key={key}
                    onDoubleClick={() => addNode(key)}
                    onClick={() => addNode(key)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-900 transition-colors flex items-center gap-2"
                    title={def.desc}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: def.color, flexShrink: 0 }} />
                    <span style={{ color: def.color, fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>
                      {def.label}
                    </span>
                  </button>
                ))}
            </div>
          ))}
          <div className="px-3 py-2 border-t border-zinc-800 mt-2">
            <div className="text-[9px] text-zinc-600 leading-relaxed">
              Click para agregar un nodo al canvas
            </div>
          </div>
        </aside>
      )}

      {/* Canvas */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="px-4 py-2 border-b border-zinc-800 bg-zinc-950 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowPalette(p => !p)}
            className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-zinc-300"
          >
            {showPalette ? "◀ Palette" : "▶ Palette"}
          </button>
          <div className="h-4 w-px bg-zinc-800 mx-1" />
          <Button
            onClick={deleteSelected}
            variant="ghost"
            className="rounded-none text-zinc-400 hover:text-red-400 border border-zinc-800 gap-1 h-7 text-xs px-2"
          >
            <Trash size={12} /> Eliminar seleccionado
          </Button>
          <Button
            onClick={handleGenerateCode}
            variant="ghost"
            className="rounded-none text-cyan-300 border border-zinc-800 hover:bg-zinc-900 gap-1 h-7 text-xs px-2"
          >
            <Code size={12} /> Generar código
          </Button>
          <Button
            onClick={handleSave}
            className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold gap-1 h-7 text-xs px-3 ml-auto"
          >
            Guardar script
          </Button>
        </div>

        <div style={{ flex: 1, position: "relative" }}>
          <ReactFlow
            nodes={nodesWithCallbacks}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={NODE_TYPES}
            fitView
            deleteKeyCode="Delete"
            style={{ background: "#09090b" }}
          >
            <Background color="#27272a" gap={24} size={1} />
            <Controls style={{ background: "#18181b", border: "1px solid #27272a" }} />
            <MiniMap
              style={{ background: "#09090b", border: "1px solid #27272a" }}
              nodeColor={n => NODE_DEFS[n.data?.type]?.color || "#3f3f46"}
            />
          </ReactFlow>

          {/* Help overlay */}
          <div className="absolute bottom-14 left-4 text-[9px] font-mono text-zinc-700 pointer-events-none">
            Click en la paleta para agregar · Arrastra puertos para conectar · Del para eliminar
          </div>
        </div>

        {/* Generated code panel */}
        {showCode && generatedCode && (
          <div className="border-t border-zinc-800 bg-zinc-950" style={{ maxHeight: 220 }}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
              <span className="text-[10px] tracking-[0.3em] uppercase text-cyan-400">Código generado</span>
              <div className="flex gap-2">
                <button onClick={handleCopyCode} className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-zinc-200">Copiar</button>
                <button onClick={handleDownloadCode} className="text-[10px] tracking-[0.2em] uppercase text-cyan-400 hover:text-cyan-300">Descargar .js</button>
                <button onClick={() => setShowCode(false)} className="text-zinc-500 hover:text-zinc-300 ml-2">✕</button>
              </div>
            </div>
            <pre className="p-4 text-xs font-mono text-zinc-300 overflow-auto gf-scroll" style={{ maxHeight: 160 }}>
              <code>{generatedCode}</code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}